import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, reload, signOut as firebaseSignOut } from 'firebase/auth';
import { auth } from './firebase';
import { sendPasswordReset } from './firebaseAuth';
import { getIdToken } from 'firebase/auth';
import { clearSessionData, setSessionData } from '../store/sessionDataStore';
import { clearTokenCache, fetchAllKpis } from '../api/kpiApi';

const AuthContext = createContext(null);

/* ── Session-level profile cache (survives re-renders, not page reload) ── */
const PROFILE_CACHE_KEY = 'sol_user_profile';
const PROFILE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function _getCachedProfile(uid) {
  try {
    const raw = sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const { uid: cachedUid, ts, data } = JSON.parse(raw);
    if (cachedUid !== uid) return null;
    if (Date.now() - ts > PROFILE_CACHE_TTL) return null;
    return data;
  } catch { return null; }
}

function _setCachedProfile(uid, data) {
  try {
    sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify({ uid, ts: Date.now(), data }));
  } catch { /* quota exceeded – ignore */ }
}

function _clearCachedProfile() {
  try { sessionStorage.removeItem(PROFILE_CACHE_KEY); } catch { }
}

/* ── Module-level Firebase ID token cache (50 s TTL) ── */
let _idTokenCache = { token: null, expiresAt: 0 };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const fetchingRef = useRef(false);   // deduplicate concurrent calls

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      // Reset token caches on auth-user changes to avoid stale bearer reuse.
      _idTokenCache = { token: null, expiresAt: 0 };
      clearTokenCache();
      setUser(u);
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const getIdTokenForApi = async (forceRefresh = false) => {
    if (!auth.currentUser) throw new Error('No signed-in user.');
    const now = Date.now();
    if (!forceRefresh && _idTokenCache.token && now < _idTokenCache.expiresAt) {
      console.log('[token] CACHE HIT (skipped Firebase call)');
      return _idTokenCache.token;
    }
    console.time('[token] Firebase getIdToken');
    const token = await getIdToken(auth.currentUser, forceRefresh);
    console.timeEnd('[token] Firebase getIdToken');
    _idTokenCache = { token, expiresAt: now + 50_000 }; // 50 s cache
    return token;
  };

  const fetchWithAuthRetry = async (url, options = {}) => {
    let token = await getIdTokenForApi(false);
    let res = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });

    if (res.status !== 401) return res;

    // Retry once with forced token refresh to recover from stale/revoked token.
    token = await getIdTokenForApi(true);
    return fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const refreshProfile = async (force = false) => {
    if (!auth.currentUser) {
      setProfile(null);
      _clearCachedProfile();
      return null;
    }

    const uid = auth.currentUser.uid;

    // Serve from session cache unless forced
    if (!force) {
      const cached = _getCachedProfile(uid);
      if (cached) {
        console.log('[profile] CACHE HIT (skipped /api/users/me)');
        setProfile(cached);
        return cached;
      }
    }

    // Deduplicate — don't fire two /me calls in parallel
    if (fetchingRef.current) return profile;
    fetchingRef.current = true;

    setProfileLoading(true);
    setProfileError('');
    console.time('[profile] fetch /api/users/me');
    try {
      const res = await fetchWithAuthRetry(`${apiBaseUrl}/api/users/me`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.detail || `Failed to load profile (${res.status})`);
      }
      const data = await res.json();
      console.timeEnd('[profile] fetch /api/users/me');
      setProfile(data);
      _setCachedProfile(uid, data);
      return data;
    } catch (e) {
      console.timeEnd('[profile] fetch /api/users/me');
      setProfile(null);
      setProfileError(e?.message || 'Failed to load profile');
      return null;
    } finally {
      setProfileLoading(false);
      fetchingRef.current = false;
    }
  };

  const prefetchSessionData = async () => {
    if (!auth.currentUser) return;
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;

      // fetchAllKpis handles token, timeout, AND in-flight dedup:
      // if loadAll (KpiDashboard) already fired the same request, this
      // reuses that in-flight promise — zero extra HTTP calls or DB queries.
      const t0 = performance.now();
      const all = await fetchAllKpis(year, month, 'monthly', null);
      console.log(`[prefetch] /api/kpi/all: ${Math.round(performance.now() - t0)}ms`);

      const kpiKey = `kpi:${year}:${month}:monthly:`;
      setSessionData(kpiKey, { ...all, cachedAt: Date.now() });
      console.log(`[prefetch] KPI data cached under key: ${kpiKey}`);
    } catch {
      // Prefetch best-effort only — never block login.
    }
  };

  useEffect(() => {
    // Load profile first, then prefetch KPI data only for non-uploader roles.
    refreshProfile().then((p) => {
      if (!p) return;
      // Fire login audit (best-effort)
      fetchWithAuthRetry(`${apiBaseUrl}/api/users/me/audit-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: p.role, email: p.email }),
        }).catch(() => {});
      if (p.role !== 'uploader') {
        prefetchSessionData();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  const refreshUser = async () => {
    if (!auth.currentUser) return;
    await reload(auth.currentUser);
    setUser(auth.currentUser);
    return auth.currentUser;
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    setProfile(null);
    setProfileError('');
    _clearCachedProfile();
    clearSessionData();
    _idTokenCache = { token: null, expiresAt: 0 };
    clearTokenCache();
  };

  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const isUploader = profile?.role === 'uploader';
  const canEditSourceConfig = !!profile?.can_edit_source_config;
  // canUpload: pure uploaders, users with can_upload flag, and admins
  const canUpload = isUploader || !!profile?.can_upload || isAdmin;
  const kpiAccess = profile?.kpi_access || {};

  const value = useMemo(
    () => ({
      user,
      authLoading,
      profile,
      profileLoading,
      profileError,
      refreshProfile,
      isAdmin,
      isUploader,
      canUpload,
      canEditSourceConfig,
      kpiAccess,
      refreshUser,
      sendPasswordReset,
      signOut,
      getIdToken: getIdTokenForApi,
      fetchWithAuthRetry,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, authLoading, profile, profileLoading, profileError, isAdmin, isUploader, canUpload, canEditSourceConfig, kpiAccess]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
